import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Caption1,
  Button,
  Dropdown,
  Option,
  Label,
} from '@fluentui/react-components';
import {
  ArrowRightFilled,
  ArrowDownFilled,
  ArrowUpFilled,
  PeopleFilled,
  AppsFilled,
} from '@fluentui/react-icons';
import { useAuth } from '../auth/TeamsAuthProvider';
import { QualityMetricsCard } from './QualityMetricsCard';
import { TrendChart } from './TrendChart';
import { BranchHealthGrid } from './BranchHealthGrid';
import { NativeDateInput } from '../common/NativeDateInput';
import { TimezoneSelector } from '../common/TimezoneSelector';
import { useTimezone } from '../common/TimezoneProvider';
import { PageHeader } from '../common/PageHeader';
import { ViewAsAction } from '../common/ViewAsAction';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { QualityIndicator } from '../common/QualityIndicator';
import { KpiTile } from '../common/KpiTile';
import type { KpiTileTone, KpiTileSubItem } from '../common/KpiTile';
import { DurationAggregateDisplay } from '../common/SourceValueDisplay';
import { api } from '../../services/api';
import type { DashboardSummary, TrendData, BranchQualityResponse, CallVolume, BadCallStats } from '../../types';

const useStyles = makeStyles({
  content: {
    padding: tokens.spacingHorizontalL,
    maxWidth: '1400px',
    margin: '0 auto',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'end',
    width: '100%',
    marginBottom: tokens.spacingVerticalL,
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  filterAction: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  wideFilter: {
    gridColumn: 'span 2',
  },
  cardsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
  },
  chartRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
  },
  branchInfo: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  tileWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: '280px',
    flex: '1 1 280px',
  },
  tileFootnote: {
    color: tokens.colorNeutralForeground3,
  },
});

/** Was `BadCallPercentageCard.tsx`'s `getOverallStatus` -- kept as-is, just relocated. */
export function getOverallStatus(percentage: number): 'good' | 'warning' | 'poor' {
  if (percentage > 5) return 'poor';
  if (percentage > 2) return 'warning';
  return 'good';
}

/** Maps 1:1 onto `KpiTileTone` (spec §4.3, amended 2026-09-13, revised same day to add the `warning` state). */
export function overallStatusToTone(status: 'good' | 'warning' | 'poor'): KpiTileTone {
  if (status === 'good') return 'positive';
  if (status === 'poor') return 'negative';
  return 'warning';
}

/**
 * Replaces `CallVolumeCard.tsx` (retired -- spec §4.3, amended 2026-09-13).
 * The average-duration line has no slot in `KpiTile`'s shape, so it renders
 * as a small caption under the tile, outside it -- the same pattern
 * `MyDashboard.tsx`'s data-quality line uses.
 */
function CallVolumeTile({ data, isLoading }: { data: CallVolume | null; isLoading?: boolean }) {
  const styles = useStyles();
  if (isLoading || !data) {
    return (
      <div className={styles.tileWrap}>
        <KpiTile size="hero" label="Call Volume" value="" loading />
      </div>
    );
  }
  const total = data.inbound_calls + data.outbound_calls;
  const inPct = total > 0 ? Math.round((data.inbound_calls / total) * 100) : 0;
  const outPct = total > 0 ? Math.round((data.outbound_calls / total) * 100) : 0;
  const subItems: KpiTileSubItem[] = [
    { icon: <ArrowDownFilled />, label: 'Inbound', value: data.inbound_calls.toLocaleString() },
    { icon: <ArrowUpFilled />, label: 'Outbound', value: data.outbound_calls.toLocaleString() },
    { icon: <PeopleFilled />, label: 'Direct to Users', value: data.user_calls.toLocaleString() },
    { icon: <AppsFilled />, label: 'Main-line calls', value: (data.call_queue_calls + data.auto_attendant_calls).toLocaleString() },
  ];
  return (
    <div className={styles.tileWrap}>
      <KpiTile
        size="hero"
        label="Call Volume"
        value={data.total_calls}
        caption={`${inPct}% Inbound : ${outPct}% Outbound`}
        subItems={subItems}
      />
      <Caption1 className={styles.tileFootnote}>
        <DurationAggregateDisplay
          averageSeconds={data.avg_duration_seconds}
          knownCount={data.duration_known_count}
          totalCount={data.duration_total_count}
          isPartial={data.duration_is_partial}
          completionFallbackCount={data.completion_fallback_count}
        />
      </Caption1>
    </div>
  );
}

/**
 * Replaces `BadCallPercentageCard.tsx` (retired -- spec §4.3, amended
 * 2026-09-13). Each breakdown row's `QualityIndicator` icon already carries
 * its own status color, so `subItems[].status` is left unset here to avoid
 * coloring the same row twice.
 */
function BadCallTile({ data, isLoading }: { data: BadCallStats | null; isLoading?: boolean }) {
  const styles = useStyles();
  if (isLoading || !data) {
    return (
      <div className={styles.tileWrap}>
        <KpiTile size="hero" label="Bad Calls" value="" loading />
      </div>
    );
  }
  const status = getOverallStatus(data.bad_call_percentage);
  const subItems: KpiTileSubItem[] = [
    {
      icon: <QualityIndicator status={data.bad_jitter_count > 0 ? 'poor' : 'good'} size="small" />,
      label: 'High Jitter',
      value: data.bad_jitter_count,
    },
    {
      icon: <QualityIndicator status={data.bad_latency_count > 0 ? 'poor' : 'good'} size="small" />,
      label: 'High Latency',
      value: data.bad_latency_count,
    },
    {
      icon: <QualityIndicator status={data.bad_packet_loss_count > 0 ? 'poor' : 'good'} size="small" />,
      label: 'Packet Loss',
      value: data.bad_packet_loss_count,
    },
  ];
  return (
    <div className={styles.tileWrap}>
      <KpiTile
        size="hero"
        label="Bad Calls"
        value={`${data.bad_call_percentage.toFixed(1)}%`}
        valueTone={overallStatusToTone(status)}
        caption={`${data.bad_calls.toLocaleString()} of ${data.total_calls.toLocaleString()} calls`}
        subItems={subItems}
      />
    </div>
  );
}

type DatePreset = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';

function getTimeZoneCalendarDate(timeZone: string, baseDate = new Date()): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(baseDate);

  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '1970');
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '1');
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '1');

  return new Date(Date.UTC(year, month - 1, day));
}

function formatDateParam(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function buildPresetRange(preset: Exclude<DatePreset, 'custom'>, timeZone: string) {
  if (preset === 'all') {
    return { startDate: '', endDate: '' };
  }

  const endDate = getTimeZoneCalendarDate(timeZone);
  const startDate = new Date(endDate);

  if (preset === 'yesterday') {
    startDate.setUTCDate(startDate.getUTCDate() - 1);
    endDate.setUTCDate(endDate.getUTCDate() - 1);
  } else if (preset === 'last7') {
    startDate.setUTCDate(startDate.getUTCDate() - 6);
  } else if (preset === 'last30') {
    startDate.setUTCDate(startDate.getUTCDate() - 29);
  }

  return {
    startDate: formatDateParam(startDate),
    endDate: formatDateParam(endDate),
  };
}

function inferDatePreset(startDate: string, endDate: string, timeZone: string): DatePreset {
  if (!startDate && !endDate) {
    return 'all';
  }

  for (const preset of ['today', 'yesterday', 'last7', 'last30'] as const) {
    const range = buildPresetRange(preset, timeZone);
    if (range.startDate === startDate && range.endDate === endDate) {
      return preset;
    }
  }

  return 'custom';
}

function getStoredDatePreset(searchParams: URLSearchParams, startDate: string, endDate: string, timeZone: string): DatePreset {
  const preset = searchParams.get('date_preset');
  if (preset === 'all' || preset === 'today' || preset === 'yesterday' || preset === 'last7' || preset === 'last30' || preset === 'custom') {
    return preset;
  }
  return inferDatePreset(startDate, endDate, timeZone);
}

// Helper to get default date range (last 7 days)
export const ALL_CUSTOMERS = '__all__';

export interface DashboardCustomerScope {
  /** Customer name to send to the API; undefined means every customer. */
  effectiveCustomer: string | undefined;
  /** The customer the picker should display ('' = All Customers). */
  selectedCustomer: string;
  /** Internal user has not chosen yet: render the picker, run no query. */
  awaitingChoice: boolean;
}

// Decided with the owner 2026-09-20: an internal (Logic V) user never gets
// the all-customers scan by default. It stays available as an explicit
// choice (`customer_name=__all__`), because it is a full read of every
// tenant's radacct rows and grows every day. External users are always
// pinned to their own customer.
export function resolveDashboardCustomerScope(
  customerParam: string | null,
  user: { is_internal?: boolean; customer_filter?: string | null } | null | undefined,
): DashboardCustomerScope {
  if (user?.is_internal === false) {
    return {
      effectiveCustomer: user.customer_filter || undefined,
      selectedCustomer: user.customer_filter || '',
      awaitingChoice: false,
    };
  }
  if (!customerParam) {
    return { effectiveCustomer: undefined, selectedCustomer: '', awaitingChoice: true };
  }
  if (customerParam === ALL_CUSTOMERS) {
    return { effectiveCustomer: undefined, selectedCustomer: '', awaitingChoice: false };
  }
  return { effectiveCustomer: customerParam, selectedCustomer: customerParam, awaitingChoice: false };
}

function getDefaultDateRange(timeZone: string) {
  return buildPresetRange('last7', timeZone);
}

interface DashboardProps {
  emulationPanelOpen: boolean;
  onToggleEmulationPanel: () => void;
}

export function Dashboard({ emulationPanelOpen, onToggleEmulationPanel }: DashboardProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { formatDate, timezone } = useTimezone();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [trends, setTrends] = useState<TrendData | null>(null);
  const [branchQuality, setBranchQuality] = useState<BranchQualityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [branchQualityLoading, setBranchQualityLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // Filter state
  const [customers, setCustomers] = useState<string[]>([]);
  const [allBranches, setAllBranches] = useState<{ name: string; customer_name?: string }[]>([]);
  const defaultDates = getDefaultDateRange(timezone);
  const customerScope = resolveDashboardCustomerScope(searchParams.get('customer_name'), user);
  const { selectedCustomer, effectiveCustomer, awaitingChoice } = customerScope;
  const selectedBranch = searchParams.get('branch_name') || '';
  const startDate = searchParams.get('start_date') || defaultDates.startDate;
  const endDate = searchParams.get('end_date') || defaultDates.endDate;
  const currentDatePreset = getStoredDatePreset(searchParams, startDate, endDate, timezone);
  const [showCustomDates, setShowCustomDates] = useState(currentDatePreset === 'custom');

  // Filter branches by selected customer
  const filteredBranches = selectedCustomer
    ? allBranches.filter(b => !b.customer_name || b.customer_name === selectedCustomer)
    : allBranches;

  const updateDashboardFilters = useCallback((updates: Record<string, string | undefined>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Fetch customers and branches list
  useEffect(() => {
    async function fetchFilterOptions() {
      if (!isAuthenticated) return;
      try {
        const [customerList, branchList] = await Promise.all([
          api.getCustomers(),
          api.getBranches(),
        ]);
        setCustomers(customerList);
        setAllBranches(branchList);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    }
    fetchFilterOptions();
  }, [isAuthenticated]);

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    const requestId = ++requestIdRef.current;
    if (awaitingChoice) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [summaryData, trendsData] = await Promise.all([
        api.getDashboardSummary(startDate || undefined, endDate || undefined, effectiveCustomer, selectedBranch || undefined, timezone),
        api.getTrends(startDate || undefined, endDate || undefined, 'day', effectiveCustomer, selectedBranch || undefined, timezone),
      ]);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setSummary(summaryData);
      setTrends(trendsData);
      setError(null);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, awaitingChoice, startDate, endDate, effectiveCustomer, selectedBranch, timezone]);

  // Fetch branch quality data
  const fetchBranchQuality = useCallback(async () => {
    if (!isAuthenticated || awaitingChoice) return;

    try {
      setBranchQualityLoading(true);
      const branchQualityRes = await api.getBranchQuality(
        startDate || undefined,
        endDate || undefined,
        effectiveCustomer,
        timezone,
      );
      setBranchQuality(branchQualityRes);
    } catch (err) {
      // Branch quality is optional; don't fail the whole dashboard if it errors
      console.warn('Failed to fetch branch quality data:', err);
      setBranchQuality(null);
    } finally {
      setBranchQualityLoading(false);
    }
  }, [isAuthenticated, awaitingChoice, effectiveCustomer, startDate, endDate, timezone]);

  useEffect(() => {
    fetchData();
    fetchBranchQuality();
  }, [fetchData, fetchBranchQuality]);

  useEffect(() => {
    const hasDates = searchParams.has('start_date') || searchParams.has('end_date');
    if (!hasDates) {
      updateDashboardFilters({
        date_preset: 'last7',
        start_date: defaultDates.startDate,
        end_date: defaultDates.endDate,
      });
    }
  }, [defaultDates.endDate, defaultDates.startDate, searchParams, updateDashboardFilters]);

  useEffect(() => {
    if (currentDatePreset === 'custom') {
      setShowCustomDates(true);
    }
  }, [currentDatePreset]);

  const handleDatePresetChange = (preset: string | undefined) => {
    const normalizedPreset = (preset || 'all') as DatePreset;

    if (normalizedPreset === 'custom') {
      setShowCustomDates(true);
      if (!startDate && !endDate) {
        const range = buildPresetRange('today', timezone);
        updateDashboardFilters({
          date_preset: 'custom',
          start_date: range.startDate,
          end_date: range.endDate,
        });
      } else {
        updateDashboardFilters({ date_preset: 'custom' });
      }
      return;
    }

    setShowCustomDates(false);
    const range = buildPresetRange(normalizedPreset, timezone);
    updateDashboardFilters({
      date_preset: normalizedPreset,
      start_date: range.startDate || undefined,
      end_date: range.endDate || undefined,
    });
  };

  if (!isAuthenticated) {
    return <LoadingState variant="page" />;
  }

  const periodLabel = summary
    ? `${formatDate(summary.period_start)} - ${formatDate(summary.period_end)}`
    : '';

  const header = (
    <PageHeader
      title="Call Quality Dashboard"
      subtitle={periodLabel || undefined}
      breadcrumb="Dashboard"
      actions={
        <ViewAsAction
          emulationPanelOpen={emulationPanelOpen}
          onToggleEmulationPanel={onToggleEmulationPanel}
        />
      }
    />
  );

  if (error) {
    return (
      <div>
        {header}
        <div className={styles.content}>
          <ErrorState
            message={error}
            onRetry={() => {
              fetchData();
              fetchBranchQuality();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className={styles.content}>
        <div className={styles.filters}>
          {user?.is_internal !== false && (
            <div className={styles.filterGroup}>
              <Label size="small">Customer</Label>
              <Dropdown
                placeholder="Choose a customer"
                value={awaitingChoice ? '' : selectedCustomer || 'All Customers'}
                onOptionSelect={(_, data) => {
                  updateDashboardFilters({
                    customer_name: data.optionValue === ALL_CUSTOMERS ? ALL_CUSTOMERS : data.optionValue || undefined,
                    branch_name: undefined,
                  });
                }}
                style={{ minWidth: '180px' }}
              >
                <Option key={ALL_CUSTOMERS} value={ALL_CUSTOMERS}>All Customers</Option>
                {customers.map((customer) => (
                  <Option key={customer} value={customer}>
                    {customer}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )}
          {filteredBranches.length > 0 && (
            <div className={styles.filterGroup}>
              <Label size="small">{user?.location_label_singular ?? 'Branch'}</Label>
              <Dropdown
                placeholder={`All ${user?.location_label_plural ?? 'Branches'}`}
                value={selectedBranch || `All ${user?.location_label_plural ?? 'Branches'}`}
                onOptionSelect={(_, data) => {
                  updateDashboardFilters({
                    branch_name: data.optionValue === '__all__' ? undefined : data.optionValue || undefined,
                  });
                }}
                style={{ minWidth: '180px' }}
              >
                <Option key="__all__" value="__all__" text={`All ${user?.location_label_plural ?? 'Branches'}`}>
                  All {user?.location_label_plural ?? 'Branches'}
                </Option>
                {filteredBranches.map((branch) => (
                  <Option key={branch.name} value={branch.name}>
                    {branch.name}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )}
          <div className={styles.filterGroup}>
            <Label size="small">Date Range</Label>
            <Dropdown
              value={
                currentDatePreset === 'all' ? 'All Time'
                  : currentDatePreset === 'today' ? 'Today'
                  : currentDatePreset === 'yesterday' ? 'Yesterday'
                  : currentDatePreset === 'last7' ? 'Last 7 Days'
                  : currentDatePreset === 'last30' ? 'Last 30 Days'
                  : 'Custom'
              }
              onOptionSelect={(_, data) => handleDatePresetChange(data.optionValue)}
              style={{ minWidth: '180px' }}
            >
              <Option value="all">All Time</Option>
              <Option value="today">Today</Option>
              <Option value="yesterday">Yesterday</Option>
              <Option value="last7">Last 7 Days</Option>
              <Option value="last30">Last 30 Days</Option>
              <Option value="custom">Custom</Option>
            </Dropdown>
          </div>
          {showCustomDates && (
            <div className={styles.filterGroup}>
              <Label size="small">Start Date</Label>
              <NativeDateInput
                value={startDate}
                max={endDate || undefined}
                onChange={(value) => updateDashboardFilters({
                  date_preset: 'custom',
                  start_date: value || undefined,
                })}
              />
            </div>
          )}
          {showCustomDates && (
            <div className={styles.filterGroup}>
              <Label size="small">End Date</Label>
              <NativeDateInput
                value={endDate}
                min={startDate || undefined}
                onChange={(value) => updateDashboardFilters({
                  date_preset: 'custom',
                  end_date: value || undefined,
                })}
              />
            </div>
          )}
          <div className={styles.filterGroup}>
            <TimezoneSelector />
          </div>
          <div className={styles.filterAction}>
            <Button
              appearance="primary"
              icon={<ArrowRightFilled />}
              iconPosition="after"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                if (user?.is_internal === false && user.customer_filter) {
                  params.set('customer_name', user.customer_filter);
                }
                navigate(`/calls${params.toString() ? `?${params.toString()}` : ''}`);
              }}
            >
              View All Calls
            </Button>
          </div>
        </div>

      {user && !user.is_internal && user.customer_filter && (
        <div className={styles.branchInfo}>
          <Text>
            Viewing data for: <strong>{user.customer_filter}</strong>
          </Text>
        </div>
      )}

      {user && user.is_internal && !user.is_admin && user.branch_names.length > 0 && (
        <div className={styles.branchInfo}>
          <Text>
            Showing data for: <strong>{user.branch_names.join(', ')}</strong>
          </Text>
        </div>
      )}

      {awaitingChoice ? (
        <EmptyState
          data-testid="dashboard-choose-customer"
          title="Choose a customer to load the dashboard"
          hint={`Pick a customer above. "All Customers" reads every tenant's call history for the period and takes noticeably longer.`}
        />
      ) : (
      <>
      <div className={styles.cardsRow}>
        <CallVolumeTile
          data={summary?.call_volume ?? null}
          isLoading={isLoading}
        />
        <QualityMetricsCard
          data={summary?.quality_metrics ?? null}
          isLoading={isLoading}
        />
        <BadCallTile
          data={summary?.bad_call_stats ?? null}
          isLoading={isLoading}
        />
      </div>

      <div className={styles.chartRow}>
        <TrendChart data={trends} isLoading={isLoading} />
      </div>

      <div className={styles.chartRow}>
        <BranchHealthGrid data={branchQuality?.branches || null} isLoading={branchQualityLoading} />
      </div>
      </>
      )}
      </div>
    </div>
  );
}
